<script setup lang="ts">
import { type FragmentType, graphql } from '@/gql';
import { useRoutes } from '@glow/shared/lib/routes';
import { useAuthModalStore } from '@glow/shared/features/auth';
import { useToasts } from '@glow/shared/store/toasts';
import { formatPrice } from '@glow/shared/utils/format';
import { formatDuration } from 'date-fns';

export type PartnerServicePreviewInjectData =
  | {
      type: 'master';
      workPlaceId: number;
    }
  | { type: 'company' };

const partnerServiceFragment = graphql(/* GraphQL */ `
  fragment PartnerServicePreview_PartnerServiceFragment on PartnerService {
    id
    price
    duration
    description
    isLiked
    attachedPhotos {
      id
      url
    }
    service {
      id
      slug
      name
    }
    masters {
      id
      slug
    }
    company {
      id
      slug
    }
    parameters {
      id
    }
  }
`);

const props = withDefaults(
  defineProps<{
    service: FragmentType<typeof partnerServiceFragment>;
    canBeLiked?: boolean;
    inCart?: boolean;
  }>(),
  {
    canBeLiked: false,
    inCart: false,
  }
);

const addressIndex = inject<Readonly<Ref<number>>>('partnerAddressIndex');

const { ensureLoggedIn } = useAuthModalStore();
const { getUrl } = useRoutes();
const service = useFragmentData(partnerServiceFragment, () => props.service);
const { staticUrl } = useRuntimeConfig().public;
const { showSuccessToast } = useToasts();
const { showLikedToast } = useContextToasts();
const coverUrl = toRef(
  () =>
    service.value.attachedPhotos[0]?.url ??
    `${staticUrl}/images/services/${service.value.service.id}.jpg`
);
const href = computed(() =>
  getUrl(
    'partnerService',
    service.value.company
      ? {
          city: 'moscow',
          partnerType: 'company',
          partnerSlug: service.value.company.slug,
          service: service.value.service.slug,
        }
      : {
          city: 'moscow',
          partnerType: 'master',
          partnerSlug: service.value.masters[0].slug,
          service: service.value.service.slug,
        }
  )
);

const { executeMutation: updateServiceLikedState } = useMutation(
  graphql(/* GraphQL */ `
    mutation MasterServicesTab_UpdatePartnerServiceLikedStateMutation(
      $id: Int!
      $newIsLiked: Boolean!
    ) {
      updatePartnerServiceLikedState(id: $id, newIsLiked: $newIsLiked) {
        id
        isLiked
      }
    }
  `)
);

const { executeMutation: addToCart, fetching: isAddingToCart } = useMutation(
  graphql(/* GraphQL */ `
    mutation MasterServicesTab_AddMasterServiceToCartMutation(
      $partnerServiceId: Int!
      $workPlaceId: Int!
      $input: AddServiceToCartInput!
    ) {
      addPartnerServiceToCart(
        partnerServiceId: $partnerServiceId
        workPlaceId: $workPlaceId
        input: $input
      ) {
        id
      }
    }
  `)
);

const isAddToCartModalVisible = ref(false);

async function onAddToCart() {
  if (!addressIndex) return;
  if (partnerData.value.type === 'master' && !service.value.parameters.length) {
    await addToCart({
      partnerServiceId: service.value.id,
      workPlaceId: partnerData.value.workPlaceId,
      input: { parameterIds: [] },
    });
    showSuccessToast({ title: 'Добавлено в корзину' });
  } else isAddToCartModalVisible.value = true;
}

function onLikedStateUpdate(newIsLiked: boolean) {
  ensureLoggedIn(async () => {
    await updateServiceLikedState({ id: service.value.id, newIsLiked });
    showLikedToast(newIsLiked);
  });
}
</script>

<template>
  <div class="page-card p-3 service-preview">
    <div class="service-preview__image">
      <NuxtLink class="service-preview__image-wrapper" :to="href">
        <NuxtImg format="webp" :src="coverUrl" />
      </NuxtLink>
      <AppCheckbox
        v-if="props.canBeLiked"
        variant="like"
        color="light"
        class="service-preview__like"
        :model-value="service.isLiked"
        @update:model-value="onLikedStateUpdate"
      />
    </div>
    <NuxtLink class="text-black font-bold" :to="href">
      {{ service.service.name }}
    </NuxtLink>
    <div class="flex-1">{{ service.description }}</div>
    <div class="d-flex justify-content-between gap-2">
      <span class="text-black-secondary">Длительность</span>
      <span class="font-bold">{{ service.duration }} мин.</span>
    </div>
    <div class="d-flex justify-content-between gap-2">
      <span class="text-black-secondary">Стоимость</span>
      <span class="font-bold">
        {{ formatPrice(service.price) }}
      </span>
    </div>
    <AppButton
      class="service-preview__button"
      :loading="isAddingToCart"
      border-radius="sm"
      fluid
      @click="onAddToCart"
    >
      <template v-if="inCart">В корзине</template>
      <template v-else>Добавить услугу</template>
    </AppButton>
  </div>
</template>

<style lang="scss" scoped>
.service-preview {
  display: flex;
  flex-direction: column;
  gap: var(--app-spacing-3);
  background: var(--app-color-accent-tertiary);

  &__image {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--app-color-black-secondary);
    border-radius: var(--app-br-sm);
    aspect-ratio: 1.13;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__image-wrapper {
    width: 100%;
    height: 100%;
  }

  &__like {
    position: absolute;
    top: 12px;
    right: 12px;
  }

  &__price {
    margin-top: 4px;
    font-size: 15px;
    line-height: 22px;
    color: var(--app-color-black-secondary);

    @include media-breakpoint-down(sm) {
      font-weight: 500;
      font-size: 13px;
      line-height: 16px;
    }
  }
}
</style>
